import { useState } from "react";
import { formatDateTime } from "@/lib/format";
import { useTranslation } from "react-i18next";

import { PlatformPageHeader } from "@/components/PlatformLayout";
import {
  usePlatformAuditEvents,
  usePlatformBulkAnchor,
  usePlatformChainVerify,
  usePlatformStampNow,
} from "@/lib/platform";

const KINDS: { value: string; labelKey: string }[] = [
  { value: "", labelKey: "platform.audit.kindAny" },
  { value: "org.viewed", labelKey: "platform.audit.kindOrgViewed" },
  { value: "org.suspended", labelKey: "platform.audit.kindOrgSuspended" },
  { value: "org.reinstated", labelKey: "platform.audit.kindOrgReinstated" },
  { value: "kyc.status_changed", labelKey: "platform.audit.kindKybChanged" },
  { value: "audit.force_anchor", labelKey: "platform.org.forceSubmit" },
];

export default function PlatformAuditEventsPage() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<string>("");
  const events = usePlatformAuditEvents({
    kind: kind || undefined,
    limit: 100,
  });
  const verify = usePlatformChainVerify();

  return (
    <div className="space-y-4">
      <PlatformPageHeader
        title={t("platform.audit.title")}
        description={t("platform.audit.desc")}
      />

      {/* Chain verify summary — green when intact, red when tampered. */}
      {verify.data && (
        <div
          className={`rounded-md border p-3 text-sm ${
            verify.data.ok
              ? "border-nb-ok/40 bg-nb-ok/5 text-nb-ok"
              : "border-nb-danger/40 bg-nb-danger/5 text-nb-danger"
          }`}
          data-testid="chain-verify-banner"
          role="status"
        >
          {verify.data.ok ? (
            <span>
              ✓ {t("platform.audit.chainIntact", { count: verify.data.total })}{" "}
              <code className="font-mono text-xs">
                {verify.data.chain_head_hash.slice(0, 12)}…
              </code>
            </span>
          ) : (
            <span>
              ✗ {t("platform.audit.chainTamperedPre", { seq: verify.data.broken?.sequence })}{" "}
              <code className="font-mono text-xs">
                {verify.data.broken?.expected_hash.slice(0, 12)}…
              </code>{" "}
              {t("platform.audit.chainTamperedMid")}{" "}
              <code className="font-mono text-xs">
                {verify.data.broken?.stored_hash.slice(0, 12)}…
              </code>
              .
            </span>
          )}
        </div>
      )}

      <div className="flex gap-3 rounded-md border border-nb-border bg-nb-surface p-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-nb-mute">{t("platform.audit.kindLabel")}</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="rounded-md border border-nb-border bg-nb-bg px-2 py-1"
            data-testid="audit-kind-filter"
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {t(k.labelKey)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <BulkAnchorCard />
      <PlatformChainStampCard />

      {events.isLoading && (
        <div className="text-sm text-nb-mute">{t("common.loading")}</div>
      )}
      {events.isError && (
        <div className="text-sm text-nb-danger">
          {t("platform.audit.loadError")}
        </div>
      )}

      {events.data && (
        <ul className="space-y-2">
          {events.data.results.map((ev) => (
            <li
              key={ev.id}
              className="rounded-md border border-nb-border bg-nb-surface p-3 text-sm"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-xs text-nb-mute">
                  {formatDateTime(ev.occurred_at)}
                </span>
                <span className="rounded bg-nb-bg px-2 py-0.5 font-mono text-xs">
                  {ev.kind}
                </span>
              </div>
              <div className="mt-1">
                <span className="text-nb-mute">{t("platform.audit.actor")}</span>{" "}
                {ev.actor_user_email || "—"}{" "}
                <span className="text-nb-mute">{t("platform.audit.target")}</span>{" "}
                {ev.target_org_name || t("platform.audit.none")}
              </div>
              {Object.keys(ev.payload).length > 0 && (
                <pre className="mt-2 overflow-x-auto rounded bg-nb-bg p-2 text-xs">
                  {JSON.stringify(ev.payload, null, 2)}
                </pre>
              )}
            </li>
          ))}
          {events.data.results.length === 0 && (
            <li className="text-sm text-nb-mute">{t("platform.audit.noEvents")}</li>
          )}
        </ul>
      )}
    </div>
  );
}


/** Bulk force-anchor sessions ended in a date range. Useful after a
 *  TSA outage when the per-session sweep needs a date-bounded retry. */
function BulkAnchorCard() {
  const { t } = useTranslation();
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const bulk = usePlatformBulkAnchor();
  return (
    <section
      className="rounded-md border border-nb-border bg-nb-surface p-3"
      aria-labelledby="bulk-anchor-heading"
    >
      <h2
        id="bulk-anchor-heading"
        className="mb-2 text-sm font-semibold uppercase tracking-wide text-nb-mute"
      >
        {t("platform.audit.bulkHeading")}
      </h2>
      <p className="mb-2 text-xs text-nb-mute">
        {t("platform.audit.bulkDescPre")} <code>ended_at</code>{" "}
        {t("platform.audit.bulkDescPost")}
      </p>
      <form
        className="flex flex-wrap items-end gap-2 text-sm"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!since || !until) return;
          await bulk.mutateAsync({
            since: new Date(since).toISOString(),
            until: new Date(until).toISOString(),
          });
        }}
      >
        <label className="flex flex-col">
          <span className="text-xs text-nb-mute">{t("platform.audit.since")}</span>
          <input
            type="datetime-local"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="rounded-md border border-nb-border bg-nb-bg px-2 py-1"
            data-testid="bulk-anchor-since"
          />
        </label>
        <label className="flex flex-col">
          <span className="text-xs text-nb-mute">{t("platform.audit.until")}</span>
          <input
            type="datetime-local"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="rounded-md border border-nb-border bg-nb-bg px-2 py-1"
            data-testid="bulk-anchor-until"
          />
        </label>
        <button
          type="submit"
          className="btn-secondary"
          disabled={!since || !until || bulk.isPending}
          data-testid="bulk-anchor-submit"
        >
          {bulk.isPending ? t("platform.common.anchoring") : t("platform.audit.bulkSubmit")}
        </button>
      </form>
      {bulk.isError && (
        <div className="mt-2 text-xs text-nb-danger" role="alert">
          {t("platform.audit.bulkError")}
        </div>
      )}
      {bulk.isSuccess && bulk.data && (
        <div className="mt-2 text-xs text-nb-ok" role="status">
          {t("platform.audit.bulkResult", {
            attempted: bulk.data.sessions_attempted,
            succeeded: bulk.data.sessions_succeeded,
            failed: bulk.data.sessions_failed,
            tokens: bulk.data.tokens_touched,
          })}
          {bulk.data.truncated_at_limit && t("platform.audit.truncated")}
        </div>
      )}
    </section>
  );
}


/** Manually anchor the platform-staff audit chain head. */
function PlatformChainStampCard() {
  const { t } = useTranslation();
  const stamp = usePlatformStampNow();
  return (
    <section className="rounded-md border border-nb-border bg-nb-surface p-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-mute">
            {t("platform.audit.stampHeading")}
          </h2>
          <p className="text-xs text-nb-mute">
            {t("platform.audit.stampDesc")}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          disabled={stamp.isPending}
          onClick={() => stamp.mutate()}
          data-testid="platform-chain-stamp-now"
        >
          {stamp.isPending ? t("platform.common.anchoring") : t("platform.audit.stampNow")}
        </button>
      </div>
      {stamp.isSuccess && stamp.data && (
        <div className="mt-2 text-xs text-nb-ok" role="status">
          {t("platform.audit.stampSuccess", {
            count: stamp.data.stamped,
            detail: stamp.data.tokens
              .map((tok) => `${tok.backend}:${tok.state}`)
              .join(", "),
          })}
        </div>
      )}
      {stamp.isError && (
        <div className="mt-2 text-xs text-nb-danger" role="alert">
          {t("platform.audit.stampError")}
        </div>
      )}
    </section>
  );
}
